import { VComparable } from "../../types/asset-class/comparable";
import { IncomeValuationProcess } from "../../types/valuation-process";
import { durationCalculation } from "../../../../valuation";
import { avgEffectives, avgEffectivesBua, avgNonRecoverable, avgRecoverable } from "../comparables";
import { round2, yieldPurchaseOnPerpetuityAnnually, yieldPurchaseOnPerpetuityQuarterly, miniFs, yieldPurchaseUntilReviewAnnually, yieldPurchaseUntilReviewQuarterly, rounddown4 } from "./utils";
import * as moment from "moment";
import { InspectionLandParcelComponent } from "src/app/views/pages/target-property/inspection-base/inspection-land-parcel/inspection-land-parcel.component";
import { RentReviewTypes } from "src/app/core/v2/types";

export function uploadComparableFile(
  process: IncomeValuationProcess,
  data: {
    name: string,
    path: string
  }
): IncomeValuationProcess {
  return {
    ...process,
    data: {
      ...process.data,
      comparableFileName: data.name,
      comparableFilePath: data.path
    }
  }
}

export function updateModelAssumptionOfValueAsAWhole(
  process: IncomeValuationProcess,
  data: {
    passingRent: number,
    grossMarketRentAssessment: 'comp-page' | 'upload',
    grossMarketRent: number,
    leasableAreas: {
      id: number,
      propertySubTypeId: number,
      leasableArea: number
    }[],
    methodId: number,
    timingId: number,
    changeToGrossMarketDefault: boolean,
    capitalRate: number,
    purchaserCost: number,
    estimatedVoidPeriod: number,
    opex: number,
    voidPeriodAllowance: number,
    startDate: Date,
    leaseDuration: number,
    rentReviewTypeId: number,
    rentReviewCycle: number,
    writeOffPeriodAfter: number,
    breakOptionAfter: number,
    activeBreakOption: boolean,
    growthRatePerYear: number,
    allRiskYield: number,
    reversionaryYield: number,
    fixedRentIncreaseType: string,
    fixedRentIncreaseNumber: number,
    fixedRentIncreasePercentage: number,
  }
): IncomeValuationProcess {
  return {
    ...process,
    data: {
      ...process.data,
      passingRent: data.passingRent,
      grossMarketRentAssessment: data.grossMarketRentAssessment,
      grossMarketrent: data.grossMarketRent,
      leasableAreas: data.leasableAreas,
      methodId: data.methodId,
      timingId: data.timingId,
      capitalRate: data.capitalRate,
      purchaserCost: data.purchaserCost,
      estimatedVoidPeriod: data.estimatedVoidPeriod,
      opex: data.opex,
      voidPeriodAllowance: data.voidPeriodAllowance,
      startDate: data.startDate,
      leaseDuration: data.leaseDuration,
      rentReviewTypeId: data.rentReviewTypeId,
      rentReviewCycle: data.rentReviewCycle,
      writeOffPeriodAfter: data.writeOffPeriodAfter,
      breakOptionAfter: data.breakOptionAfter,
      activeBreakOption: data.activeBreakOption,
      growthRatePerYear: data.growthRatePerYear,
      allRiskYield: data.allRiskYield,
      reversionaryYield: data.reversionaryYield,
      fixedRentIncreaseType: data.fixedRentIncreaseType,
      fixedRentIncreasePercentage: data.fixedRentIncreasePercentage,
      fixedRentIncreaseNumber: data.fixedRentIncreaseNumber
    }
  }
}

export function valueAsWholeComparableAverages(
  allComparables: VComparable[],
  selectedConsiderations: {refNum: string, id: number}[],
  countryCurrency: string
) {
  const grouped = Object.entries(
    allComparables.reduce((acc, com) => {
      if (!acc[com.propertySubTypeName]) {
        acc[com.propertySubTypeName] = []
      } 
      acc[com.propertySubTypeName].push(com)
      return acc
    }, {} as Record<string, VComparable[]>)
  ).map(([propertySubType, comparables]) => {
    return {
      propertySubTypeName: propertySubType,
      comparables
    }
  })

  return grouped.map(({propertySubTypeName, comparables}) => {
    const considerationAndSizes = comparables.map(comparable => {
      const selectedConsideration = selectedConsiderations.find(item => item.refNum === comparable.refNum)
      if (selectedConsideration !== undefined) {
        return {
          refNum: comparable.refNum,
          consideration: comparable.considerations.find(item => item.id === selectedConsideration.id),
          size: comparable.sizes.length > 0 ? comparable.sizes[0].size : 0
        }
      }
      if (comparable.considerations.length > 0) {
        return {
          refNum: comparable.refNum,
          consideration: comparable.considerations[0],
          size: comparable.sizes.length > 0 ? comparable.sizes[0].size : 0
        }
      }
      return undefined
    })
    const considerations = considerationAndSizes.filter(item => item !== undefined)

    const leaseDuration = considerations.reduce((acc, item) => {
      if (item.consideration.considerationType === 'Rent') {
        const leaseDuration = durationCalculation(item.consideration.oldTenure.lease_duration, item.consideration.oldTenure.duration_type)
        return {
          total: acc.total + Number(leaseDuration),
          num: acc.num + 1
        }
      }
      return acc
    }, {
      total: 0,
      num: 0
    })

    const rentReview = considerations.reduce((acc, item) => {
      if (item.consideration.considerationType === 'Rent') {
        const rentReview = durationCalculation(item.consideration.oldTenure.rent_review, item.consideration.oldTenure.duration_type)
        return {
          total: acc.total + Number(rentReview),
          num: acc.num + 1
        }
      }
      return acc
    }, {total: 0, num: 0})

    const rentFreePeriod = considerations.reduce((acc, item) => {
      if (item.consideration.considerationType === 'Rent') {
        const rentReview = durationCalculation(item.consideration.oldTenure.rent_free_period, item.consideration.oldTenure.duration_type)
        return {
          total: acc.total + Number(rentReview),
          num: acc.num + 1
        }
      }
      return acc
    }, {total: 0, num: 0})

    const fittingOutPeriod = considerations.reduce((acc, item) => {
      if (item.consideration.considerationType === 'Rent') {
        const rentReview = durationCalculation(item.consideration.oldTenure.fitting_out_period, item.consideration.oldTenure.duration_type)
        return {
          total: acc.total + Number(rentReview),
          num: acc.num + 1
        }
      }
      return acc
    }, {total: 0, num: 0})

    const recoverable = avgRecoverable(considerationAndSizes, countryCurrency)
    const nonRecoverable = avgNonRecoverable(considerationAndSizes, countryCurrency)

    return {
      propertySubTypeName,
      effectiveRent: avgEffectives(considerationAndSizes, countryCurrency),
      bua: avgEffectivesBua(considerationAndSizes, countryCurrency),
      leaseDuration: leaseDuration.num > 0 ? (leaseDuration.total / leaseDuration.num) : undefined,
      rentReview: rentReview.num > 0 ? (rentReview.total / rentReview.num) : undefined,
      recoverable: recoverable.num > 0 ? (recoverable.total / recoverable.num) : undefined,
      nonRecoverable: nonRecoverable.num > 0 ? (nonRecoverable.total / nonRecoverable.num) : undefined,
      rentFreePeriod: rentFreePeriod.num > 0 ? (rentFreePeriod.total / rentFreePeriod.num) : undefined,
      fittingOutPeriod: fittingOutPeriod.num > 0 ? (fittingOutPeriod.total / fittingOutPeriod.num) : undefined
    }
  })
}

export function calculateSuggestedGrossMarketRent(
  analysiedComparables: {
    propertySubTypeName: string,
    bua: {
      value: number
    }
  }[],
  leasableAreas: {
    propertySubTypeId: number,
    leasableArea: number
  }[],
  propertySubTypes: {
    id: number,
    name: string
  }[]
) {
  const grouped = Object.values(
    leasableAreas.reduce(
      (acc, item) => {
        if (!acc[item.propertySubTypeId]) {
          acc[item.propertySubTypeId] = { propertySubTypeId: item.propertySubTypeId, leasableArea: 0 }
        }
        acc[item.propertySubTypeId].leasableArea += item.leasableArea
        return acc
      },
      {} as Record<number, {propertySubTypeId: number, leasableArea: number}>
    )
  )
  const converteds = grouped.map(la => {
    const name = propertySubTypes.find(item => item.id === la.propertySubTypeId)?.name
    const comparable = analysiedComparables.find(com => com.propertySubTypeName === name) 
    const bua = comparable?.bua.value
    return {
      propertySubTypeName: name,
      bua,
      leasableArea: la.leasableArea,
      value: bua * la.leasableArea
    }
  })
  const value = converteds.reduce(
    (acc, item) => {
      return acc + item.bua * item.leasableArea
    },
    0
  )

  return {
    value: value,
    properties: converteds
  }
}

export function calculateFreeholdCapOnPerpetuity(
  grossMarketRentPerAnnum: number,
  opex: number,
  voidPeriodAllowance: number,
  capitalRate: number,
  purchaserCost: number,
  timingId: number
) {
  const operatingExpenditures = (opex / 100) * grossMarketRentPerAnnum
  const voidRate = (voidPeriodAllowance / 100) * grossMarketRentPerAnnum
  const allowances = operatingExpenditures + voidRate

  const noi = grossMarketRentPerAnnum - allowances
  const yieldPurchaseOnPerpetuityAtCapitalRate = timingId === 1
    ? yieldPurchaseOnPerpetuityAnnually(capitalRate)
    : timingId === 2
      ? yieldPurchaseOnPerpetuityQuarterly(capitalRate)
      : undefined
  const capitalisedRent = yieldPurchaseOnPerpetuityAtCapitalRate * noi
  const totalPurchaserCost = (purchaserCost/100)  * capitalisedRent
  const value = capitalisedRent - totalPurchaserCost

  return {
    marketRent: isNaN(grossMarketRentPerAnnum) ? undefined : grossMarketRentPerAnnum,
    allowances: isNaN(allowances) ? undefined : allowances,
    operatingExpenditures: isNaN(operatingExpenditures) ? undefined : operatingExpenditures,
    voidRate: isNaN(voidRate) ? undefined : voidRate,
    noi: isNaN(noi) ? undefined : noi,
    yieldPurchaseOnPerpetuityAtCapitalRate,
    capitalisedRent: isNaN(capitalisedRent) ? undefined : capitalisedRent,
    totalPurchaserCost: isNaN(totalPurchaserCost) ? undefined : totalPurchaserCost,
    value: isNaN(value) ? undefined : value,
  }
}

export function calculateTermAndReversionHardcoreLayer(
  passingRentPerAnnum: number,
  grossMarketRentPerAnnum: number,
  opex: number,
  voidPeriodAllowance: number,
  activeBreakOffOption: boolean,
  valuationDate: Date,
  startDate: Date,
  leaseDuration: number,
  rentReviewTypeId: number,
  rentReviewCycle: number,
  rentReviewDetailType: string,
  rentReviewDetail: number,
  writeOffPeriodAfter: number,
  breakOptionAfter: number,
  allRiskYield: number,
  reversionaryYield: number,
  estimatedVoidPeriod: number,
  growthRate: number,
  purchaserCost: number,
  timingId: number
) {
  const unexpiredLeaseTerm = valuationDate ? calculateUnexpiredLeaseTerm(startDate, leaseDuration, valuationDate, true) : null
  const yearsToReview = valuationDate ? calculateYearsToReview(startDate, leaseDuration, valuationDate, rentReviewCycle, true) : null
  const yearsToWriteOff = valuationDate ? calculateYearsToWriteOff(startDate, leaseDuration, valuationDate, writeOffPeriodAfter, true) : null
  const yearsToBreakOff = valuationDate ? calculateYearsToBreakOff(startDate, valuationDate, activeBreakOffOption, breakOptionAfter, true) : null

  const operatingExpenditures = (opex / 100) * passingRentPerAnnum
  const voidRate = (voidPeriodAllowance / 100) * passingRentPerAnnum
  const allowances = operatingExpenditures + voidRate

  const noiTerm = passingRentPerAnnum - allowances
  const minYear = miniFs(
    activeBreakOffOption
      ? {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff, 'Years to Break off': yearsToBreakOff}
      : {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff}
  )
  const yieldPurchaseUntilReview = timingId == 1 
    ? yieldPurchaseUntilReviewAnnually(allRiskYield, rounddown4(minYear.value))
    : timingId == 2
      ? yieldPurchaseUntilReviewQuarterly(allRiskYield, rounddown4(minYear.value))
      : undefined
  const capitalValueOfTerm = yieldPurchaseUntilReview * noiTerm

  const noiReversion = grossMarketRentPerAnnum - (grossMarketRentPerAnnum * ((opex + voidPeriodAllowance)/100))
  const yieldPurchaseOnPerpetuity = timingId == 1
    ? yieldPurchaseOnPerpetuityAnnually(reversionaryYield)
    : timingId == 2
      ? yieldPurchaseOnPerpetuityQuarterly(reversionaryYield)
      : undefined

  const _presentValue = presentValue(
    reversionaryYield,
    activeBreakOffOption,
    estimatedVoidPeriod,
    breakOptionAfter,
    unexpiredLeaseTerm,
    minYear
  ) 
  const _presentValueGrowth = presentValueGrowth(growthRate, activeBreakOffOption, estimatedVoidPeriod, breakOptionAfter, unexpiredLeaseTerm, minYear)
  const _capitalValueOfReversion = capitalValueOfReversion(
    passingRentPerAnnum,
    grossMarketRentPerAnnum, 
    noiReversion,
    rentReviewTypeId,
    rentReviewDetailType,
    rentReviewDetail,
    _presentValue.value, 
    yieldPurchaseOnPerpetuity, 
    _presentValueGrowth.value 
  )

  const capitalValueOfTermAndReversion = capitalValueOfTerm + _capitalValueOfReversion;
  const totalPurchaserCost = capitalValueOfTermAndReversion
    ? purchaserCost
      ? (purchaserCost / 100) * capitalValueOfTermAndReversion
      : 0
    : undefined
  const value = capitalValueOfTermAndReversion - totalPurchaserCost

  return {
    passingRent: isNaN(passingRentPerAnnum) ? undefined : passingRentPerAnnum,
    marketRent: isNaN(grossMarketRentPerAnnum) ? undefined : grossMarketRentPerAnnum,
    allowances: isNaN(allowances) ? undefined : allowances,
    operatingExpenditures: isNaN(operatingExpenditures) ? undefined : operatingExpenditures,
    voidRate: isNaN(voidRate) ? undefined : voidRate,

    noiTerm: isNaN(noiTerm) ? undefined : noiTerm,
    yieldPurchaseUntilReview: {
      value: yieldPurchaseUntilReview,
      year: {
        value: round2(minYear.value),
        key: minYear.key
      }
    },
    capitalValueOfTerm: isNaN(capitalValueOfTerm) ? undefined : capitalValueOfTerm,
    
    noiReversion: isNaN(noiReversion) ? undefined : noiReversion,
    yieldPurchaseOnPerpetuity,

    presentValue: {
      value: _presentValue.value,
      year: {
        value: round2(_presentValue.year.value),
        year1: _presentValue.year.year1,
        year1Value: round2(_presentValue.year.year1Value),
        year2: _presentValue.year.year2,
        year2Value: _presentValue.year.year2Value ? round2(_presentValue.year.year2Value) : undefined
      } 
    },
    presentValueGrowth: {
      value: _presentValueGrowth.value,
      year: {
        value: round2(_presentValueGrowth.year.value),
        year1: _presentValueGrowth.year.year1,
        year1Value: round2(_presentValueGrowth.year.year1Value),
        year2: _presentValueGrowth.year.year2,
        year2Value: _presentValueGrowth.year.year2Value ? round2(_presentValueGrowth.year.year2Value) : undefined
      } 
    },
    capitalValueOfReversion: isNaN(_capitalValueOfReversion) ? undefined : _capitalValueOfReversion,
    capitalValueOfTermAndReversion: isNaN(capitalValueOfTermAndReversion) ? undefined : capitalValueOfTermAndReversion,
    totalPurchaserCost: isNaN(totalPurchaserCost) ? undefined : totalPurchaserCost,
    value: isNaN(value) ? undefined : value
  }
}

export function calculateTermAndReversionHardcoreFroth(
  passingRentPerAnnum: number,
  grossMarketRentPerAnnum: number,
  opex: number,
  voidPeriodAllowance: number,
  activeBreakOffOption: boolean,
  valuationDate: Date,
  startDate: Date,
  leaseDuration: number,
  rentReviewCycle: number,
  writeOffPeriodAfter: number,
  breakOptionAfter: number,
  allRiskYield: number,
  reversionaryYield: number,
  purchaserCost: number,
  timingId: number
) {
  const unexpiredLeaseTerm = valuationDate ? calculateUnexpiredLeaseTerm(startDate, leaseDuration, valuationDate, true) : null
  const yearsToReview = valuationDate ? calculateYearsToReview(startDate, leaseDuration, valuationDate, rentReviewCycle, true) : null
  const yearsToWriteOff = valuationDate ? calculateYearsToWriteOff(startDate, leaseDuration, valuationDate, writeOffPeriodAfter, true) : null
  const yearsToBreakOff = valuationDate ? calculateYearsToBreakOff(startDate, valuationDate, activeBreakOffOption, breakOptionAfter, true) : null

  const operatingExpenditures = (opex / 100) * passingRentPerAnnum
  const voidRate = (voidPeriodAllowance / 100) * passingRentPerAnnum
  const allowances = operatingExpenditures + voidRate

  const noiTerm = passingRentPerAnnum - allowances
  const yieldPurchaseOnPerpetuity = timingId == 1
    ? yieldPurchaseOnPerpetuityAnnually(allRiskYield)
    : timingId == 2
      ? yieldPurchaseOnPerpetuityQuarterly(allRiskYield)
      : undefined
  const capitalValueOfTerm = yieldPurchaseOnPerpetuity * (grossMarketRentPerAnnum - (grossMarketRentPerAnnum * ((opex + voidPeriodAllowance)/ 100))) 

  const excessRent = noiTerm - (grossMarketRentPerAnnum - (grossMarketRentPerAnnum * ((opex + voidPeriodAllowance)/ 100)))
  const minYear = miniFs(
    activeBreakOffOption
      ? {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff, 'Years to Break off': yearsToBreakOff}
      : {'Years to Review': yearsToReview, 'Years to Write off': yearsToWriteOff}
  )
  const yieldPurchaseUntilReview = timingId == 1 
    ? yieldPurchaseUntilReviewAnnually(reversionaryYield, minYear.value)
    : timingId == 2
      ? yieldPurchaseUntilReviewQuarterly(reversionaryYield, minYear.value)
      : undefined
  const capitalValueOfExcessRent = yieldPurchaseUntilReview * excessRent

  const capitalValueOfTermAndExcess = capitalValueOfTerm + capitalValueOfExcessRent
  const totalPurchaserCost = capitalValueOfTermAndExcess
    ? purchaserCost
      ? (purchaserCost / 100) * capitalValueOfTermAndExcess
      : 0
    : undefined
  const value = capitalValueOfTermAndExcess - totalPurchaserCost

  return {
    passingRent: isNaN(passingRentPerAnnum) ? undefined : passingRentPerAnnum,
    marketRent: isNaN(grossMarketRentPerAnnum) ? undefined : grossMarketRentPerAnnum,
    allowances: isNaN(allowances) ? undefined : allowances,
    operatingExpenditures: isNaN(operatingExpenditures) ? undefined : operatingExpenditures,
    voidRate: isNaN(voidRate) ? undefined : voidRate,

    noiTerm: isNaN(noiTerm) ? undefined : noiTerm,
    yieldPurchaseOnPerpetuity,
    capitalValueOfTerm: isNaN(capitalValueOfTerm) ? undefined : capitalValueOfTerm,

    excessRent: isNaN(excessRent) ? undefined : excessRent,
    yieldPurchaseUntilReview: {
      value: yieldPurchaseUntilReview,
      year: {
        value: round2(minYear.value),
        key: minYear.key
      }
    },
    capitalValueOfExcessRent : isNaN(capitalValueOfExcessRent) ? undefined : capitalValueOfExcessRent,

    capitalValueOfTermAndExcess: isNaN(capitalValueOfTermAndExcess) ? undefined : capitalValueOfTermAndExcess,
    totalPurchaserCost: isNaN(totalPurchaserCost) ? undefined : totalPurchaserCost,
    value: isNaN(value) ? undefined : value
  }
}

export function calculateUnexpiredLeaseTerm(
  startDate: Date,
  leaseDuration: number,
  today: Date,
  notRound: boolean = false 
) {
  if (!today) return null;
  if (!startDate) return leaseDuration
  const startDateMoment = moment(startDate)

  const date = startDateMoment.clone().add(
    leaseDuration * 12,
    'M'
  )

  const todayMoment = moment(today)

  if (date.isBefore(todayMoment)) {
    return 0;
  }

  const yearDiff = _yearsDiff(todayMoment, date)
  const monthDiff = _monthsDiff(todayMoment, date, 'unexpired')
  const value = yearDiff + (monthDiff / 12)
  return notRound 
    ? value
    : round2(value)
}

export function calculateYearsToReview(
  startDate: Date,
  leaseDuration: number,
  today: Date,
  rentReviewCycle: number,
  notRound: boolean = false
) {
  if (!today) return null;
  if (!rentReviewCycle) return calculateUnexpiredLeaseTerm(startDate, leaseDuration, today, notRound);
  if (!startDate) return rentReviewCycle;

  const startDateMoment = moment(startDate);
  const todayMoment = moment(today)

  const year = startDateMoment.year() + (Math.floor((todayMoment.year() - startDateMoment.year()) / rentReviewCycle) + 1) * rentReviewCycle
  const date = moment({
    year,
    month: startDateMoment.month(),
    day: startDateMoment.date()
  })

  if (date.isBefore(todayMoment)) {
    return 0;
  }

  const yearDiff = _yearsDiff(todayMoment, date)
  const monthDiff = _monthsDiff(todayMoment, date)
  const value = yearDiff + monthDiff / 12
  return notRound
    ? value
    : round2(value)
}

export function calculateYearsToWriteOff(
  startDate: Date,
  leaseDuration: number,
  today: Date,
  writeOffPeriodAfter: number,
  notRound: boolean = false
) {
  if (!today) return null
  if (!writeOffPeriodAfter) return calculateUnexpiredLeaseTerm(startDate, leaseDuration, today, notRound);
  if (!startDate) return writeOffPeriodAfter;

  const startDateMoment = moment(startDate);
  const todayMoment = moment(today)
    
  const year = startDateMoment.year() + (Math.floor((todayMoment.year() - startDateMoment.year()) / writeOffPeriodAfter) + 1) * writeOffPeriodAfter
  const date = moment({
    year,
    month: startDateMoment.month(),
    day: startDateMoment.date()
  })
  const yearDiff = _yearsDiff(todayMoment, date) 
  const monthDiff = _monthsDiff(todayMoment, date)
  const value = yearDiff + monthDiff / 12
  return notRound
    ? value
    : round2(value)
}

export function calculateYearsToBreakOff(
  startDate: Date,
  today: Date,
  activeBreakOption: boolean,
  breakOptionAfter: number,
  notRound: boolean = false
) {
  if (!today) return null;
  if (!activeBreakOption) return 0;
  if (!breakOptionAfter) return 0;

  const startDateMoment = moment(startDate);
  const todayMoment = moment(today)

  const date = startDateMoment.clone().add(
    breakOptionAfter * 12,
    'M'
  )

  if (date.isBefore(todayMoment)) {
    return 0;
  }

  const yearDiff = _yearsDiff(todayMoment, date)
  const monthDiff = _monthsDiff(todayMoment, date, 'unexpired')
  const value = yearDiff + (monthDiff / 12)
  return notRound 
    ? value
    : round2(value)
}

function _monthsDiff(startDate: moment.Moment, endDate: moment.Moment, label?: string) {
  const yearsDiff = endDate.year() - startDate.year()
  const monthsDiff = endDate.month() - startDate.month()
  let totalMonths = yearsDiff * 12 + monthsDiff;
  if (endDate.date() < startDate.date()) {
    totalMonths--
  }
  return totalMonths % 12
}

function _yearsDiff(startDate: moment.Moment, endDate: moment.Moment) {
  return Math.abs(startDate.diff(endDate.add(1, 'day'), 'years'))
}

function presentValue(
  reversionaryYield: number, 
  activeBreakOffOption: boolean, 
  estimatedVoidPeriod: number,
  breakOffOption: number, 
  unexpiredLeaseTerm: number,
  minYear: {key: string, value: number}
) {
  const year = activeBreakOffOption
    ? {value: breakOffOption + estimatedVoidPeriod, year1: 'Break option after', year1Value: breakOffOption, year2: 'Estimated Void period', year2Value: estimatedVoidPeriod}
    : minYear.value === unexpiredLeaseTerm
      ? {value: minYear.value + estimatedVoidPeriod, year1: minYear.key, year1Value: minYear.value, year2: 'Estimated Void period', year2Value: estimatedVoidPeriod}
      : {value: minYear.value, year1: minYear.key, year1Value: minYear.value}

  if (year.value === null) return {value: undefined, year}
  const rateInVal = reversionaryYield / 100
  if (rateInVal === 0) return {value: undefined, year}
  let temp = 1 + rateInVal
  temp = Math.pow(temp, -rounddown4(year.value))
  return {value: rounddown4(temp), year}
}

function presentValueGrowth(
  growthRate: number, 
  activeBreakOffOption: boolean, 
  estimatedVoidPeriod: number,
  breakOffOption: number, 
  unexpiredLeaseTerm: number,
  minYear: {key: string, value: number}
) {
  const year = activeBreakOffOption
    ? {value: breakOffOption + estimatedVoidPeriod, year1: 'Break option after', year1Value: breakOffOption, year2: 'Estimated Void period', year2Value: estimatedVoidPeriod}
    : minYear.value === unexpiredLeaseTerm
      ? {value: minYear.value + estimatedVoidPeriod, year1: minYear.key, year1Value: minYear.value, year2: 'Estimated Void period', year2Value: estimatedVoidPeriod}
      : {value: minYear.value, year1: minYear.key, year1Value: minYear.value}

  if (year.value === null) return {value: undefined, year}
  const rateInVal = growthRate / 100
  if (rateInVal === 0) return {value: 1, year}
  let temp = 1 + rateInVal
  temp = Math.pow(temp, rounddown4(year.value))
  return {value: temp, year}
}

function capitalValueOfReversion(
  passingRent: number,
  marketRent: number,
  noiTerm: number,
  rentReviewTypeId: number,
  rentReviewDetailType: string,
  rentReviewDetail: number,
  pv: number,
  yp: number,
  pvGrowth: number
) {
  if (!rentReviewTypeId) {
    return passingRent * pv * yp
  }

  if ((marketRent > passingRent) && rentReviewTypeId == RentReviewTypes.UpwardsOnlyMarketRent) {
    return noiTerm * pv * yp * pvGrowth
  }

  if (rentReviewTypeId == RentReviewTypes.UpwardsDownwardsMarketRent) {
    return noiTerm * pv * yp * pvGrowth
  }

  if (rentReviewTypeId == RentReviewTypes.RPI) {
    return passingRent * pv * yp * pvGrowth
  }

  if (rentReviewTypeId == RentReviewTypes.CPI) {
    return passingRent * pv * yp * pvGrowth
  }

  if (rentReviewTypeId == RentReviewTypes.FixedRentIncrease) {
    if (rentReviewDetailType === 'number') {
      return (passingRent + rentReviewDetail) * yp * pv
    }
    if (rentReviewDetailType == 'percentage') {
      return (passingRent + (passingRent * rentReviewDetail / 100)) * yp * pv
    }
    return undefined
  }

  return passingRent * pv * yp
}

export function valueAsAWholeSummary(
  methodId: number,
  meta: {
    passingRent: number,
    grossMarketRent: number,
    valuationDate: Date,
    startDate: Date,
    leaseDuration: number,
    rentReviewTypeId: number,
    rentReviewCycle: number,
    writeOffPeriodAfter: number,
    breakOptionAfter: number,
    activeBreakOption: boolean,
    capitalRate: number,
    allRiskYield: number,
    reversionaryYield: number,
    estimatedVoidPeriod: number,
    growthRatePerYear: number,
    purchaserCost: number,
    opex: number,
    voidPeriodAllowance: number,
    timingId: number
  }
) {
  if (methodId == 34) {
    const result = calculateFreeholdCapOnPerpetuity(
      meta.grossMarketRent,
      meta.opex,
      meta.voidPeriodAllowance,
      meta.capitalRate,
      meta.purchaserCost,
      meta.timingId
    )

    return result.value
  }

  if (methodId == 35) {
    const result = calculateTermAndReversionHardcoreLayer(
      meta.passingRent,
      meta.grossMarketRent,
      meta.opex,
      meta.voidPeriodAllowance,
      meta.activeBreakOption,
      meta.valuationDate,
      meta.startDate,
      meta.leaseDuration,
      meta.rentReviewTypeId,
      meta.rentReviewCycle,
      'number',
      0,
      meta.writeOffPeriodAfter,
      meta.breakOptionAfter,
      meta.allRiskYield,
      meta.reversionaryYield,
      meta.estimatedVoidPeriod,
      meta.growthRatePerYear,
      meta.purchaserCost,
      meta.timingId
    )
    return result.value
  }

  if (methodId == 36) {
    const result = calculateTermAndReversionHardcoreFroth(
      meta.passingRent,
      meta.grossMarketRent,
      meta.opex,
      meta.voidPeriodAllowance,
      meta.activeBreakOption,
      meta.valuationDate,
      meta.startDate,
      meta.leaseDuration,
      meta.rentReviewCycle,
      meta.writeOffPeriodAfter,
      meta.breakOptionAfter,
      meta.allRiskYield,
      meta.reversionaryYield,
      meta.purchaserCost,
      meta.timingId
    )

    return result.value
  }

  return undefined
}